import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import savingsPolicyService from "./savingsPolicyService";
import { flashMessage as flash } from "../../../../utils/flash";

const initialState = {
  isError: false,
  isLoading: false,
  isSuccess: false,
  isUpdated: false,
  savingsPolicyConfig: null,
  customer_policy_details: null,
  single_policy: null,
  active_savings_policies: null,
  applied_savings_policies: null,
  unpaginated_applied_savings_policies: null,
  unpaginated_savings_policy: null,
  temp_policy_attach_data: null,
  covers: null,
  otherCovers: [],
};

const savingsPolicySlice = createSlice({
  initialState,
  name: "savingsPolicy",
  reducers: {
    resetPolicy: (state) => initialState,
    resetPolicyStatus: (state) => {
      state.isError = false;
      state.isSuccess = false;
      state.isLoading = false;
      state.isUpdated = false;
      state.message = "";
    },
    resetSinglePolicy: (state) => {
      state.single_policy = null;
    },
    setTempData: (state, { payload }) => {
      state.temp_policy_attach_data = payload;
    },
    resetCustomerSingleSavingsPolicyDetailsView: (state) => {
      state.customer_policy_details = null;
    },

    setOtherCovers: (state, { payload }) => {
      const newCover = state.otherCovers?.findIndex(
        (item) => item.cover_id === payload.cover_id
      );

      if (newCover >= 0) {
        state.otherCovers[newCover] = payload;
      } else {
        state.otherCovers = [...state.otherCovers, payload];
      }
    },

    setDbPolicyRisksToOthers: (state, { payload }) => {
      for (const obj of payload) {
        if (!state.otherCovers.some((obj2) => obj.cover_id === obj2.cover_id)) {
          state.otherCovers = [...state.otherCovers, ...payload];
        }
      }
    },

    deleteOtherCover: (state, { payload }) => {
      const risks = state.otherCovers.filter(
        (item) => item.cover_id !== payload
      );

      state.otherCovers = risks;
    },

    resetOtherCovers: (state) => {
      state.otherCovers = [];
    },

    resetSavingPolicyConfig: (state) => {
      state.savingsPolicyConfig = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(createSavingsPolicyConfig.pending, (state) => {
        state.isLoading = true;
        flash("loading", "Creating Savings Policy");
      })
      .addCase(createSavingsPolicyConfig.fulfilled, (state, action) => {
        state.isLoading = false;
        if (!action.payload.errors) {
          state.isSuccess = true;
          flash("success", action.payload.message);
        } else if (typeof action.payload.errors == "string") {
          flash("error", action.payload.errors);
        } else {
          let key = Object.keys(action.payload.errors)[0];
          let message = action.payload.errors[key][0];
          flash("error", message);
        }
      })
      .addCase(createSavingsPolicyConfig.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        flash("error", action.payload.message);
      })
      .addCase(getSavingsPolicies.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSavingsPolicies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.savingsPolicyConfig = action.payload.savings_policy;
        state.unpaginated_savings_policy =
          action.payload.unpaginated_savings_policy;
      })
      .addCase(getSavingsPolicies.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        flash("error", action.payload.message);
      })
      .addCase(getSingleSavingsPolicy.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSingleSavingsPolicy.fulfilled, (state, action) => {
        state.isLoading = false;
        state.single_policy = action.payload;
      })
      .addCase(getSingleSavingsPolicy.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        flash("error", "Policy cannot be accessed");
      })
      .addCase(updateSavingsPolicy.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateSavingsPolicy.fulfilled, (state, action) => {
        state.isLoading = false;
        if (!action.payload.errors) {
          state.isSuccess = true;
          flash("success", action.payload.message);
        } else if (typeof action.payload.errors == "string") {
          flash("error", action.payload.errors);
        } else {
          let key = Object.keys(action.payload.errors)[0];
          let message = action.payload.errors[key][0];
          flash("error", message);
        }
      })
      .addCase(updateSavingsPolicy.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        flash("error", action.payload.message);
      })
      .addCase(getAllActiveSavingsPolicies.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllActiveSavingsPolicies.fulfilled, (state, action) => {
        state.isLoading = false;
        if (!action.payload.errors) {
          state.isSuccess = true;
          state.active_savings_policies = action.payload;
          flash("success", action.payload.message);
        } else if (typeof action.payload.errors == "string") {
          flash("error", action.payload.errors);
        } else {
          let key = Object.keys(action.payload.errors)[0];
          let message = action.payload.errors[key][0];
          flash("error", message);
        }
      })
      .addCase(getAllActiveSavingsPolicies.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        flash("error", action.payload.message);
      })
      .addCase(getSavingsPolicyCover.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSavingsPolicyCover.fulfilled, (state, action) => {
        state.isLoading = false;
        if (!action.payload.errors) {
          state.isSuccess = true;
          state.covers = action.payload;
          flash("success", action.payload.message);
        } else if (typeof action.payload.errors == "string") {
          flash("error", action.payload.errors);
        } else {
          let key = Object.keys(action.payload.errors)[0];
          let message = action.payload.errors[key][0];
          flash("error", message);
        }
      })
      .addCase(getSavingsPolicyCover.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        flash("error", action.payload.message);
      })
      .addCase(getSavingsPolicyConfig.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSavingsPolicyConfig.fulfilled, (state, action) => {
        state.isLoading = false;
        if (!action.payload.errors) {
          state.isSuccess = true;
          state.savingsPolicyConfig = action.payload;
          flash("success", action.payload.message);
        } else if (typeof action.payload.errors == "string") {
          flash("error", action.payload.errors);
        } else {
          let key = Object.keys(action.payload.errors)[0];
          let message = action.payload.errors[key][0];
          flash("error", message);
        }
      })
      .addCase(getSavingsPolicyConfig.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        flash("error", action.payload.message);
      })
      .addCase(createCustomerSavingsPolicy.pending, (state) => {
        state.isLoading = true;
        flash("loading", "Applying Policy to Customer");
      })
      .addCase(createCustomerSavingsPolicy.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.error) {
          flash("error", action.payload.error);
        } else if (!action.payload.errors) {
          state.isUpdated = true;
          state.customer_policy_details = action.payload;
          flash("success", action.payload.message);
        } else if (typeof action.payload.errors == "string") {
          flash("error", action.payload.errors);
        } else {
          let key = Object.keys(action.payload.errors)[0];
          let message = action.payload.errors[key][0];
          flash("error", message);
        }
      })
      .addCase(createCustomerSavingsPolicy.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        flash("error", action.payload.message);
      })
      .addCase(getAppliedSavingsPolicies.pending, (state) => {
        state.isLoading = true;
        state.isFiltering = true;
      })
      .addCase(getAppliedSavingsPolicies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isFiltered = false;
        state.isFiltering = false;
        state.applied_savings_policies =
          action.payload.all_savings_applied_policies;
        // state.unpaginated_applied_savings_policies =
        //   action.payload.unpaginated_applied_policy;
        if (!action.payload.errors) {
          state.isSuccess = true;
          flash("success", action.payload.message);
        } else if (typeof action.payload.errors == "string") {
          flash("error", action.payload.errors);
        } else {
          let key = Object.keys(action.payload.errors)[0];
          let message = action.payload.errors[key][0];
          flash("error", message);
        }
      })
      .addCase(getAppliedSavingsPolicies.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        flash("error", action.payload.message);
      })
      .addCase(getSingleAppliedSavingsPolicy.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customer_policy_details = action.payload;
        if (!action.payload.errors) {
          state.isSuccess = true;
          flash("success", action.payload.message);
        } else if (typeof action.payload.errors == "string") {
          flash("error", action.payload.errors);
        } else {
          let key = Object.keys(action.payload.errors)[0];
          let message = action.payload.errors[key][0];
          flash("error", message);
        }
      })
      .addCase(getSingleAppliedSavingsPolicy.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        flash("error", action.payload.message);
      })
      // get all filtered applied policies
      .addCase(filteredSavingsPolicies.pending, (state) => {
        state.isLoading = true;
        state.isFiltering = true;
        flash("loading", "Filtering data...");
      })
      .addCase(filteredSavingsPolicies.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isFiltered = true;
        state.isFiltering = false;
        state.applied_savings_policies = payload.data;
        if (payload.errors) {
          flash("error", payload.errors);
        } else {
          flash("success", payload.message);
        }
      })
      .addCase(filteredSavingsPolicies.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isFiltering = false;
        state.message = action.payload;
        flash("error", action.payload);
      })
      .addCase(editSingleSavingsAppliedPolicy.pending, (state) => {
        state.isLoading = true;

        flash("loading", "Editing Savings Policy Application");
      })
      .addCase(editSingleSavingsAppliedPolicy.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.error) {
          flash("error", action.payload.error);
        } else if (!action.payload.errors) {
          state.isUpdated = true;
          state.customer_policy_details = action.payload;
          flash("success", action.payload.message);
        } else if (typeof action.payload.errors == "string") {
          flash("error", action.payload.errors);
        } else {
          let key = Object.keys(action.payload.errors)[0];
          let message = action.payload.errors[key][0];
          flash("error", message);
        }
      })
      .addCase(editSingleSavingsAppliedPolicy.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        flash("error", action.payload.message);
      });
  },
});

// create policy
export const createSavingsPolicyConfig = createAsyncThunk(
  "createSavingsPolicyConfig",
  async (DATA, thunkAPI) => {
    try {
      return await savingsPolicyService.createSavingsPolicyConfig(DATA);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);
// export const get all policies
export const getSavingsPolicies = createAsyncThunk(
  "getSavingsPolicies",
  async (DATA, thunkAPI) => {
    try {
      return await savingsPolicyService.getSavingsPolicies(DATA);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// get a single policy by id
export const getSingleSavingsPolicy = createAsyncThunk(
  "getSingleSavingsPolicy",
  async (payload, thunkAPI) => {
    try {
      return await savingsPolicyService.getSingleSavingsPolicy(payload);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateSavingsPolicy = createAsyncThunk(
  "updateSavingsPolicy",
  async (data, thunkAPI) => {
    try {
      return await savingsPolicyService.updateSavingsPolicy(data);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// get all active savings policy
export const getAllActiveSavingsPolicies = createAsyncThunk(
  "getAllActiveSavingsPolicies",
  async (data, thunkAPI) => {
    try {
      return await savingsPolicyService.getAllActiveSavingsPolicies(data);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// get savings policy cover
export const getSavingsPolicyCover = createAsyncThunk(
  "getSavingsPolicyCover",
  async (data, thunkAPI) => {
    try {
      return await savingsPolicyService.getSavingsPolicyCover(data);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// get savings policy config
export const getSavingsPolicyConfig = createAsyncThunk(
  "getSavingsPolicyConfig",
  async (data, thunkAPI) => {
    try {
      return await savingsPolicyService.getSavingsPolicyConfig(data);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// get savings policy config
export const createCustomerSavingsPolicy = createAsyncThunk(
  "createCustomerSavingsPolicy",
  async (data, thunkAPI) => {
    try {
      return await savingsPolicyService.createCustomerSavingsPolicy(data);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

//attach the saving policy to a customer
export const getAppliedSavingsPolicies = createAsyncThunk(
  "getAppliedSavingsPolicies",
  async (data, thunkAPI) => {
    try {
      return await savingsPolicyService.getAppliedSavingsPolicies(data);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// get single customer savings policy
export const getSingleAppliedSavingsPolicy = createAsyncThunk(
  "getSingleAppliedSavingsPolicy",
  async (data, thunkAPI) => {
    try {
      return await savingsPolicyService.getSingleAppliedSavingsPolicy(data);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

//filter all applied policies
export const filteredSavingsPolicies = createAsyncThunk(
  "filteredSavingsPolicies",
  async (data, thunkAPI) => {
    try {
      return await savingsPolicyService.filteredSavingsPolicies(data);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// edit single savings applied policy
export const editSingleSavingsAppliedPolicy = createAsyncThunk(
  "editSingleSavingsAppliedPolicy",
  async (data, thunkAPI) => {
    try {
      return await savingsPolicyService.editSingleSavingsAppliedPolicy(data);
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const {
  resetPolicy,
  resetPolicyStatus,
  resetSinglePolicy,
  setTempData,
  resetCustomerSingleSavingsPolicyDetailsView,
  setOtherCovers,
  setDbPolicyRisksToOthers,
  deleteOtherCover,
  resetOtherCovers,
  resetSavingPolicyConfig,
} = savingsPolicySlice.actions;
export default savingsPolicySlice.reducer;
